import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { db, fetchAdminUsersWithFallback } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Download, Calendar, Users, Filter, FileSpreadsheet, Loader2, Search, AlertCircle, CheckCircle2, ChevronDown, Activity, ClipboardList } from 'lucide-react';

const PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'simulate2nl';

const CLOUD_RUN_URLS: Record<string, string> = {
    adminExportData: 'https://adminexportdata-vhtfexvyrq-uc.a.run.app',
    adminExportUtilization: 'https://adminexportutilization-vhtfexvyrq-uc.a.run.app',
};

function sanitizeExportError(rawText: string, status: number): string {
    if (!rawText) return `Export failed with status ${status}. Please try again.`;
    const trimmed = rawText.trim();
    if (trimmed.startsWith('<html') || trimmed.startsWith('<!DOCTYPE') || /<title>.*Server Error.*<\/title>/i.test(trimmed)) {
        return `The export service encountered a temporary server error (HTTP ${status}). Please try again in a few seconds.`;
    }
    return trimmed;
}

async function fetchExportWithFallback(
    functionName: 'adminExportData' | 'adminExportUtilization',
    initFactory: (idToken: string) => RequestInit
): Promise<Response> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Authentication required. Please log in again.');

    const endpoints = [
        `https://us-central1-${PROJECT_ID}.cloudfunctions.net/${functionName}`,
        CLOUD_RUN_URLS[functionName],
        `/api/${functionName}`
    ].filter(Boolean);

    let lastError: Error | null = null;

    for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt > 0) {
            await new Promise(resolve => setTimeout(resolve, attempt * 1500));
        }

        const idToken = await user.getIdToken(attempt > 0);

        for (const endpoint of endpoints) {
            try {
                const response = await fetch(endpoint, initFactory(idToken));
                if (response.ok) {
                    return response;
                }

                const errorText = await response.text();
                // Do not retry 4xx auth/validation errors across other endpoints
                if (response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 429) {
                    throw new Error(sanitizeExportError(errorText, response.status));
                }

                lastError = new Error(sanitizeExportError(errorText, response.status));
                console.warn(`[ExportPortal] ${endpoint} returned ${response.status} on attempt ${attempt + 1}`);
            } catch (err: any) {
                if (err?.message && (err.message.includes('Unauthorized') || err.message.includes('Forbidden') || err.message.includes('Bad Request'))) {
                    throw err;
                }
                lastError = err instanceof Error ? err : new Error(String(err));
                console.warn(`[ExportPortal] Failed calling ${endpoint} on attempt ${attempt + 1}:`, err);
            }
        }
    }

    throw lastError || new Error('An unexpected error occurred during export.');
}

export const ExportPortal: React.FC = () => {
    const [startDate, setStartDate] = useState<string>(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState<string>(() => new Date().toISOString().split('T')[0]);
    const [teamFilters, setTeamFilters] = useState<string[]>([]);
    const [channelFilters, setChannelFilters] = useState<string[]>([]);
    const [selectedUser, setSelectedUser] = useState<string>('All');
    const [selectedForm, setSelectedForm] = useState<string>('generic'); // Default to generic, do not allow 'All'
    const [loading, setLoading] = useState(false);
    const [assessmentRubrics, setAssessmentRubrics] = useState<any[]>([]);
    const [teams, setTeams] = useState<string[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({ type: null, message: '' });

    // Menu states for fancy dropdowns
    const [teamMenuOpen, setTeamMenuOpen] = useState(false);
    const [channelMenuOpen, setChannelMenuOpen] = useState(false);
    const [teamSearch, setTeamSearch] = useState('');
    const [channelSearch, setChannelSearch] = useState('');

    const allChannels = ['Voice', 'Text'];

    useEffect(() => {
        const fetchMetadata = async () => {
            try {
                const userData = await fetchAdminUsersWithFallback();
                setUsers(userData);
                
                const uniqueTeams = Array.from(new Set(userData.map((u: any) => u.team).filter(Boolean))) as string[];
                setTeams(uniqueTeams.sort());

                // Fetch Assessment Forms
                const rubricsSnap = await getDocs(collection(db, 'assessmentRubrics'));
                const rubricsList = rubricsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setAssessmentRubrics(rubricsList);
            } catch (error) {
                console.error("Error fetching metadata:", error);
            }
        };
        fetchMetadata();
    }, []);

    const handleExport = async () => {
        setLoading(true);
        setStatus({ type: null, message: '' });
        try {
            const payload = JSON.stringify({
                startDate,
                endDate,
                team: teamFilters.length > 0 ? teamFilters : null,
                user: selectedUser === 'All' ? null : selectedUser,
                channels: channelFilters.length > 0 ? channelFilters : null,
                form: selectedForm
            });

            const response = await fetchExportWithFallback('adminExportData', (idToken) => ({
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: payload
            }));

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `export_${startDate}_to_${endDate}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            setStatus({ type: 'success', message: 'Data exported successfully!' });
        } catch (error: any) {
            console.error("Export error:", error);
            setStatus({ type: 'error', message: error.message || 'An unexpected error occurred during export.' });
        } finally {
            setLoading(false);
        }
    };

    const handleExportUtilization = async () => {
        setLoading(true);
        setStatus({ type: null, message: '' });
        try {
            const response = await fetchExportWithFallback('adminExportUtilization', (idToken) => ({
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            }));

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `user_utilization.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            setStatus({ type: 'success', message: 'Utilization exported successfully!' });
        } catch (error: any) {
            console.error("Export error:", error);
            setStatus({ type: 'error', message: error.message || 'An unexpected error occurred during export.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="export-portal-container" style={{ padding: '2rem', animation: 'fadeIn 0.4s ease-out' }}>
            <div className="card" style={{ maxWidth: '900px', margin: '0 auto', padding: '2.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
                    <div style={{ background: 'rgba(160,43,147,0.1)', padding: '0.75rem', borderRadius: '1rem' }}>
                        <FileSpreadsheet size={32} color="var(--primary)" />
                    </div>
                    <div>
                        <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#1F2937' }}>Export Data Portal</h2>
                        <p style={{ color: '#6B7280' }}>Download detailed session data with advanced filtering.</p>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    <div className="filter-group">
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#4B5563', marginBottom: '0.5rem' }}>
                            <Calendar size={14} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
                            Start Date
                        </label>
                        <input 
                            type="date" 
                            className="input-fancy" 
                            value={startDate} 
                            onChange={(e) => setStartDate(e.target.value)} 
                        />
                    </div>
                    <div className="filter-group">
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#4B5563', marginBottom: '0.5rem' }}>
                            <Calendar size={14} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
                            End Date
                        </label>
                        <input 
                            type="date" 
                            className="input-fancy" 
                            value={endDate} 
                            onChange={(e) => setEndDate(e.target.value)} 
                        />
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '2.5rem' }}>
                    {/* Team Multi-Select */}
                    <div className="filter-group" style={{ position: 'relative' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#4B5563', marginBottom: '0.5rem' }}>
                            <Users size={14} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
                            Teams
                        </label>
                        <div 
                            className="input-fancy" 
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', background: 'white' }}
                            onClick={() => setTeamMenuOpen(!teamMenuOpen)}
                        >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.9rem' }}>
                                {teamFilters.length === 0 ? 'All Teams' : `${teamFilters.length} Team${teamFilters.length > 1 ? 's' : ''}`}
                            </span>
                            <ChevronDown size={14} color="#9CA3AF" />
                        </div>

                        {teamMenuOpen && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '0.35rem', background: 'white', border: '1px solid #E5E7EB', borderRadius: '0.75rem', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 50, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ padding: '0.5rem', borderBottom: '1px solid #F3F4F6' }}>
                                    <input 
                                        placeholder="Search teams..." 
                                        value={teamSearch} 
                                        onChange={e => setTeamSearch(e.target.value)} 
                                        style={{ width: '100%', padding: '0.4rem 0.75rem', fontSize: '0.8rem', borderRadius: '0.5rem', border: '1px solid #E5E7EB', outline: 'none' }} 
                                        onClick={e => e.stopPropagation()}
                                    />
                                </div>
                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {teams.filter(t => t.toLowerCase().includes(teamSearch.toLowerCase())).map(t => {
                                        const isSel = teamFilters.includes(t);
                                        return (
                                            <div 
                                                key={t}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setTeamFilters(prev => isSel ? prev.filter(x => x !== t) : [...prev, t]);
                                                    setSelectedUser('All');
                                                }}
                                                style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem 0.8rem', cursor: 'pointer', background: isSel ? 'rgba(160,43,147,0.05)' : 'transparent', borderBottom: '1px solid #F9FAFB' }}
                                            >
                                                <input type="checkbox" checked={isSel} readOnly style={{ cursor: 'pointer' }} />
                                                <span style={{ fontSize: '0.85rem', color: isSel ? 'var(--primary)' : '#374151', fontWeight: isSel ? 600 : 400 }}>{t}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div style={{ padding: '0.5rem', borderTop: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', background: '#F9FAFB' }}>
                                    <button style={{ fontSize: '0.75rem', color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }} onClick={(e) => { e.stopPropagation(); setTeamFilters([]); }}>Clear</button>
                                    <button style={{ fontSize: '0.75rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }} onClick={(e) => { e.stopPropagation(); setTeamMenuOpen(false); }}>Done</button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Channel Multi-Select */}
                    <div className="filter-group" style={{ position: 'relative' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#4B5563', marginBottom: '0.5rem' }}>
                            <Activity size={14} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
                            Channels
                        </label>
                        <div 
                            className="input-fancy" 
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', background: 'white' }}
                            onClick={() => setChannelMenuOpen(!channelMenuOpen)}
                        >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.9rem' }}>
                                {channelFilters.length === 0 ? 'All Channels' : `${channelFilters.length} Channel${channelFilters.length > 1 ? 's' : ''}`}
                            </span>
                            <ChevronDown size={14} color="#9CA3AF" />
                        </div>

                        {channelMenuOpen && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '0.35rem', background: 'white', border: '1px solid #E5E7EB', borderRadius: '0.75rem', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 50, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ padding: '0.5rem', borderBottom: '1px solid #F3F4F6' }}>
                                    <input 
                                        placeholder="Search channels..." 
                                        value={channelSearch} 
                                        onChange={e => setChannelSearch(e.target.value)} 
                                        style={{ width: '100%', padding: '0.4rem 0.75rem', fontSize: '0.8rem', borderRadius: '0.5rem', border: '1px solid #E5E7EB', outline: 'none' }} 
                                        onClick={e => e.stopPropagation()}
                                    />
                                </div>
                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {allChannels.filter(c => c.toLowerCase().includes(channelSearch.toLowerCase())).map(c => {
                                        const isSel = channelFilters.includes(c);
                                        return (
                                            <div 
                                                key={c}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setChannelFilters(prev => isSel ? prev.filter(x => x !== c) : [...prev, c]);
                                                }}
                                                style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem 0.8rem', cursor: 'pointer', background: isSel ? 'rgba(160,43,147,0.05)' : 'transparent', borderBottom: '1px solid #F9FAFB' }}
                                            >
                                                <input type="checkbox" checked={isSel} readOnly style={{ cursor: 'pointer' }} />
                                                <span style={{ fontSize: '0.85rem', color: isSel ? 'var(--primary)' : '#374151', fontWeight: isSel ? 600 : 400 }}>{c}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div style={{ padding: '0.5rem', borderTop: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', background: '#F9FAFB' }}>
                                    <button style={{ fontSize: '0.75rem', color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }} onClick={(e) => { e.stopPropagation(); setChannelFilters([]); }}>Clear</button>
                                    <button style={{ fontSize: '0.75rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }} onClick={(e) => { e.stopPropagation(); setChannelMenuOpen(false); }}>Done</button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="filter-group">
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#4B5563', marginBottom: '0.5rem' }}>
                            <Search size={14} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
                            Specific User
                        </label>
                        <select 
                            className="input-fancy" 
                            value={selectedUser} 
                            onChange={(e) => {
                                setSelectedUser(e.target.value);
                                if (e.target.value !== 'All') setTeamFilters([]);
                            }}
                        >
                            <option value="All">All Users</option>
                            {users
                                .filter(u => teamFilters.length === 0 || teamFilters.includes(u.team))
                                .map(user => <option key={user.uid} value={user.email}>{user.fullName || user.email}</option>)}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#4B5563', marginBottom: '0.5rem' }}>
                            <ClipboardList size={14} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
                            Assessment Form
                        </label>
                        <select 
                            className="input-fancy" 
                            value={selectedForm} 
                            onChange={(e) => setSelectedForm(e.target.value)}
                        >
                            <option value="generic">Generic Form (Default)</option>
                            {assessmentRubrics.map(r => (
                                <option key={r.id} value={r.id}>{r.title}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <button 
                        className="button button-primary" 
                        style={{ padding: '1.25rem', fontSize: '1.1rem', gap: '0.75rem', justifyContent: 'center', height: 'auto', borderRadius: '1rem', boxShadow: '0 4px 12px rgba(160,43,147,0.2)' }}
                        onClick={handleExport}
                        disabled={loading}
                    >
                        {loading ? (
                            <Loader2 size={24} className="animate-spin" />
                        ) : (
                            <Download size={24} />
                        )}
                        {loading ? 'Processing Data...' : 'Download Exported CSV'}
                    </button>

                    <button 
                        className="button button-secondary" 
                        style={{ padding: '1.25rem', fontSize: '1.1rem', gap: '0.75rem', justifyContent: 'center', height: 'auto', borderRadius: '1rem', background: '#F3F4F6', color: '#374151', border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center' }}
                        onClick={handleExportUtilization}
                        disabled={loading}
                    >
                        {loading ? (
                            <Loader2 size={24} className="animate-spin" />
                        ) : (
                            <Download size={24} color="#374151" />
                        )}
                        {loading ? 'Processing Data...' : 'Export Utilization vs Allocation'}
                    </button>

                    {status.type && (
                        <div style={{ 
                            padding: '1.25rem', 
                            borderRadius: '1rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.75rem',
                            background: status.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                            color: status.type === 'success' ? '#059669' : '#DC2626',
                            border: `1px solid ${status.type === 'success' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
                            animation: 'slideUp 0.3s ease-out'
                        }}>
                            {status.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                            <span style={{ fontSize: '1rem', fontWeight: 600 }}>{status.message}</span>
                        </div>
                    )}
                </div>

                <div style={{ marginTop: '3rem', padding: '2rem', background: '#F9FAFB', borderRadius: '1.25rem', border: '1px solid #F1F3F5' }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: 800, color: '#374151', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <Filter size={16} /> Export Capabilities
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                        <ul style={{ fontSize: '0.85rem', color: '#6B7280', paddingLeft: '1.25rem', lineHeight: 1.8 }}>
                            <li>Multi-team filtering enabled.</li>
                            <li>Multi-channel selection support.</li>
                            <li>Date-range specific aggregation.</li>
                        </ul>
                        <ul style={{ fontSize: '0.85rem', color: '#6B7280', paddingLeft: '1.25rem', lineHeight: 1.8 }}>
                            <li>All core metrics and summaries.</li>
                            <li>Corrected mm:ss time formatting.</li>
                            <li>High-performance streaming export.</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};
